import React from "react";
import ReactDOM from "react-dom";
import ApiClient from "../../api/ApiClient";
import EndPoint from "../../api/Endpoints";

// Debe coincidir con la politica del backend (user.model.js PASSWORD_POLICY_REGEX):
// min 8 caracteres, 1 minuscula, 1 mayuscula, 1 numero, 1 caracter especial.
const PASSWORD_POLICY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PASSWORD_POLICY_HINT = "Mínimo 8 caracteres, con al menos una minúscula, una mayúscula, un número y un carácter especial.";

function IconKey() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3-3.5 3.5zm0 0L19 4" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}
function IconEyeOpen() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconEyeClosed() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggleShow, placeholder }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--xc-text-soft, #374151)", marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={id}
          name={id}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={{ width: "100%", boxSizing: "border-box", padding: "9px 38px 9px 12px", border: "1.5px solid var(--xc-border, #d1d5db)", borderRadius: 7, fontSize: 13.5, background: "var(--xc-elev-1, #fff)", color: "var(--xflow-ink, #0f172a)" }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          tabIndex={-1}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--xc-text-soft, #6b7280)", display: "flex" }}
        >
          {show ? <IconEyeClosed /> : <IconEyeOpen />}
        </button>
      </div>
    </div>
  );
}

function AdminResetPasswordPanel({ onClose, userId, entityLabel }) {
  const [visible, setVisible] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");

  React.useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg("");
    setSuccessMsg("");

    if (!PASSWORD_POLICY_REGEX.test(newPassword)) {
      setErrorMsg(PASSWORD_POLICY_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await ApiClient.doPut(EndPoint.adminResetPassword, { id: userId, password: newPassword });
      if (response.status) {
        setSuccessMsg(response.message_es || "Contraseña actualizada y enviada por correo.");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setErrorMsg(response.message_es || response.message || "No se pudo actualizar la contraseña.");
      }
    } catch (error) {
      setErrorMsg("No se pudo procesar la solicitud. Intenta nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, opacity: visible ? 1 : 0, transition: "opacity 0.22s ease" }} />

      <div style={{ position: "fixed", top: "50%", left: "50%", width: 380, maxWidth: "92vw", background: "var(--xflow-surface, #fff)", color: "var(--xflow-ink, #0f172a)", zIndex: 9999, borderRadius: 12, boxShadow: "var(--xflow-shadow, 0 12px 48px rgba(0,0,0,0.22))", fontFamily: '"72","72full",Arial,Helvetica,sans-serif', transform: visible ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-50%) scale(0.96)", opacity: visible ? 1 : 0, transition: "transform 0.2s ease, opacity 0.2s ease" }}>

        <div style={{ background: "linear-gradient(135deg, #0d1117 0%, #1e1b4b 100%)", color: "#f1f5f9", padding: "18px 20px", borderRadius: "12px 12px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <IconKey />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>Resetear contraseña</div>
              {entityLabel && (
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{entityLabel}</div>
              )}
            </div>
          </div>
          <button onClick={handleClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "5px 6px", display: "flex" }}>
            <IconClose />
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: "18px 20px" }}>
          {successMsg ? (
            <>
              <div style={{ background: "var(--xc-elev-1, #ecfdf5)", color: "var(--xflow-green, #047857)", border: "1.5px solid var(--xflow-green, #a7f3d0)", borderRadius: 7, padding: "10px 12px", fontSize: 13, marginBottom: 14 }}>
                {successMsg}
              </div>
              <button
                type="button"
                onClick={handleClose}
                style={{ width: "100%", padding: "9px 0", border: "none", borderRadius: 7, background: "#0891b2", color: "#fff", cursor: "pointer", fontSize: 13.5, fontWeight: 600 }}
              >
                Cerrar
              </button>
            </>
          ) : (
            <>
              <PasswordField
                id="admin-reset-new-password"
                label="Nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                show={showNew}
                onToggleShow={() => setShowNew((v) => !v)}
                placeholder="Mínimo 8 caracteres"
              />
              <p style={{ fontSize: 11.5, color: "var(--xc-text-soft, #6b7280)", marginTop: -8, marginBottom: 16 }}>{PASSWORD_POLICY_HINT}</p>

              <PasswordField
                id="admin-reset-confirm-password"
                label="Repetir nueva contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                show={showConfirm}
                onToggleShow={() => setShowConfirm((v) => !v)}
                placeholder="Repite la nueva contraseña"
              />

              {errorMsg && (
                <div style={{ background: "var(--xc-elev-1, #fef2f2)", color: "var(--xflow-rose, #b91c1c)", border: "1.5px solid var(--xflow-rose, #fecaca)", borderRadius: 7, padding: "10px 12px", fontSize: 13, marginBottom: 14 }}>
                  {errorMsg}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={handleClose}
                  style={{ padding: "8px 18px", border: "1.5px solid var(--xc-border, #d1d5db)", borderRadius: 7, background: "var(--xc-elev-1, #fff)", color: "var(--xflow-ink, #374151)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: "8px 20px", border: "none", borderRadius: 7, background: "#0891b2", color: "#fff", cursor: submitting ? "wait" : "pointer", fontSize: 13, fontWeight: 600, opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? "Actualizando..." : "Actualizar"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </>,
    document.body
  );
}

const DialogAdminResetPasswordTemplate = (props) => {
  if (!props.status || !props.id) return null;

  return (
    <AdminResetPasswordPanel
      onClose={props.close}
      userId={props.id}
      entityLabel={props.entityLabel}
    />
  );
};

export default DialogAdminResetPasswordTemplate;
